import React, { useState, useEffect, useCallback } from 'react';
import {
  Form, Input, Button, Card, Row, Col, message, Space, Typography,
  Upload,
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined, UploadOutlined, FileWordOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/axios';

const { Title, Text } = Typography;

const ProjeForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sablonDosya, setSablonDosya] = useState(null); // File object
  const [sablonAdi, setSablonAdi] = useState(null);
  const [mevcutSablon, setMevcutSablon] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const fetchData = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const res = await api.get(`/projeler/${id}`);
      const item = res.data.data || res.data;
      form.setFieldsValue(item);
      if (item.sablonAdi) setMevcutSablon(item.sablonAdi);
    } catch {
      message.error('Proje bilgileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, form]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFileUpload = (file) => {
    setSablonDosya(file);
    setSablonAdi(file.name);
    return false; // prevent auto upload
  };

  const onFinish = async (values) => {
    setSaving(true);
    try {
      // FormData kullan - şablon dosyasını multipart olarak gönder
      const formData = new FormData();
      Object.entries(values).forEach(([k, v]) => {
        if (v !== undefined && v !== null) formData.append(k, v);
      });
      if (sablonDosya) {
        formData.append('sablon', sablonDosya);
      }

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (isEdit) {
        await api.put(`/projeler/${id}`, formData, config);
        message.success('Proje güncellendi');
      } else {
        await api.post('/projeler', formData, config);
        message.success('Proje oluşturuldu');
      }
      navigate('/projeler');
    } catch (err) {
      message.error(err.response?.data?.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const sectionStyle = { marginBottom: 16, borderRadius: 8 };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/projeler')}>Geri</Button>
            <Title level={4} style={{ margin: 0 }}>{isEdit ? 'Proje Düzenle' : 'Yeni Proje Ekle'}</Title>
          </Space>
        </Col>
      </Row>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        {/* Proje Bilgileri */}
        <Card title="Proje Bilgileri" style={sectionStyle} loading={loading}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="projeAdi" label="Proje Adı" rules={[{ required: true, message: 'Zorunlu' }]}>
                <Input placeholder="Proje adını girin" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="il" label="İl">
                <Input placeholder="İl adı" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="ilce" label="İlçe">
                <Input placeholder="İlçe adı" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item name="mahalle" label="Mahalle">
                <Input placeholder="Mahalle" />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="ada" label="Ada">
                <Input placeholder="Ada" />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="parsel" label="Parsel">
                <Input placeholder="Parsel" />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item name="projeAdresi" label="Proje Adresi">
                <Input placeholder="Tam adres" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Firma Bilgileri */}
        <Card title="Firma Bilgileri" style={sectionStyle}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="firmaAdi" label="Firma Adı" rules={[{ required: true, message: 'Zorunlu' }]}>
                <Input placeholder="Firma adı" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="vergiDairesi" label="Vergi Dairesi">
                <Input placeholder="Vergi dairesi" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="vergiNo" label="Vergi No">
                <Input placeholder="Vergi no" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="firmaAdresi" label="Firma Adresi">
                <Input placeholder="Firma adresi" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="firmaTelefon" label="Telefon">
                <Input placeholder="Telefon" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="firmaEmail" label="E-Posta">
                <Input placeholder="E-posta adresi" type="email" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Şablon */}
        <Card title="Sözleşme Şablonu" style={sectionStyle}>
          {mevcutSablon && (
            <div style={{ marginBottom: 16 }}>
              <Space>
                <FileWordOutlined style={{ color: '#2b579a', fontSize: 20 }} />
                <Text>Mevcut şablon: <strong>{mevcutSablon}</strong></Text>
              </Space>
            </div>
          )}
          <Form.Item label={mevcutSablon ? 'Şablonu Değiştir (.docx)' : 'Şablon Yükle (.docx)'}>
            <Upload
              accept=".docx"
              beforeUpload={handleFileUpload}
              showUploadList={false}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>
                {sablonAdi ? sablonAdi : '.docx Dosyası Seç'}
              </Button>
            </Upload>
            {sablonAdi && (
              <Text type="success" style={{ marginLeft: 8 }}>
                {sablonAdi} seçildi
              </Text>
            )}
          </Form.Item>
        </Card>

        <Row justify="end" style={{ marginTop: 8 }}>
          <Space>
            <Button onClick={() => navigate('/projeler')}>İptal</Button>
            <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
              {isEdit ? 'Güncelle' : 'Kaydet'}
            </Button>
          </Space>
        </Row>
      </Form>
    </div>
  );
};

export default ProjeForm;
